import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout/Layout';
import Button from '../components/UI/Button';
import type { ShelterDog } from '../types/shelter';
import AnimalModal from '../components/Modals/AnimalModal';
// import AdvancedSearch from '../components/Search/AdvancedSearch'; // Commented out for future use
import { animalsAPI, getImageUrl } from '../services/api';

const Animals: React.FC = () => {
  const [animals, setAnimals] = useState<ShelterDog[]>([]);
  const [filteredAnimals, setFilteredAnimals] = useState<ShelterDog[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'available' | 'adopted' | 'hospital' | 'quarantine' | 'reserved'>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false); // Commented out for future use
  const [selectedAnimal, setSelectedAnimal] = useState<ShelterDog | null>(null);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [isSaving, setIsSaving] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [itemsPerPage] = useState(10); // Show 10 items per page
  
  // Dashboard stats state
  const [dashboardStats, setDashboardStats] = useState({
    total: 0,
    byStatus: {},
    recentAdditions: 0,
    medical: {
      vaccinated: 0,
      neutered: 0,
      chipped: 0
    }
  });

  // Load animals from API with pagination
  const loadAnimals = async (page: number = currentPage) => {
    try {
      setIsLoading(true);
      
      const params: any = {
        page: page,
        limit: itemsPerPage,
        sortBy: 'createdAt',
        sortOrder: 'desc'
      };
      
      // Add filters if they exist
      if (searchTerm) {
        params.search = searchTerm;
      }
      
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      
      const response = await animalsAPI.getAnimals(params);
      const { animals, pagination } = response.data;
      
      setAnimals(animals || []);
      setFilteredAnimals(animals || []);
      setTotalCount(pagination?.total || 0);
      setTotalPages(pagination?.totalPages || 1);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error loading animals:', error);
      setAnimals([]);
      setFilteredAnimals([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setIsLoading(false);
    }
  };

  // Load dashboard stats
  const loadDashboardStats = async () => {
    try {
      const response = await animalsAPI.getAnimalsStats();
      if (response.success) {
        setDashboardStats(response.data);
      }
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
    }
  };

  useEffect(() => {
    loadAnimals(1);
    loadDashboardStats();
  }, []);

  // Reload animals when status filter changes (not search term)
  useEffect(() => {
    setCurrentPage(1); // Reset to first page when filtering
    loadAnimals(1);
  }, [statusFilter]);

  // Search function triggered by Enter key
  const handleSearch = () => {
    setCurrentPage(1);
    loadAnimals(1);
  };

  // Handle Enter key press
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Since we're using server-side pagination and filtering,
  // filteredAnimals is the same as animals from the server
  useEffect(() => {
    setFilteredAnimals(animals);
  }, [animals]);

  const getStatusBadge = (status: string) => {
    const statusClasses = {
      available: 'bg-green-100 text-green-800',
      adopted: 'bg-primary-100 text-primary-dark',
      hospital: 'bg-red-100 text-red-800',
      quarantine: 'bg-yellow-100 text-yellow-800',
      reserved: 'bg-purple-100 text-purple-800'
    };

    const statusTexts = {
      available: 'Disponibil',
      adopted: 'Adoptat',
      hospital: 'În tratament',
      quarantine: 'În quarantină',
      reserved: 'Rezervat'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusClasses[status as keyof typeof statusClasses]}`}>
        {statusTexts[status as keyof typeof statusTexts]}
      </span>
    );
  };

  const handleDeleteAnimal = async (id: string) => {
    if (window.confirm('Sigur doriți să ștergeți acest animal?')) {
      try {
        await animalsAPI.deleteAnimal(id);
        setAnimals(prev => prev.filter(animal => animal.id !== id));
      } catch (error) {
        console.error('Error deleting animal:', error);
        alert('Eroare la ștergerea animalului');
      }
    }
  };

  const handleAddAnimal = () => {
    setSelectedAnimal(null);
    setModalMode('add');
    setIsModalOpen(true);
  };

  const handleEditAnimal = (animal: ShelterDog) => {
    setSelectedAnimal(animal);
    setModalMode('edit');
    setIsModalOpen(true);
  };

  const handleSaveAnimal = async (animalData: Partial<ShelterDog>) => {
    try {
      setIsSaving(true);
      if (modalMode === 'add') {
        const response = await animalsAPI.createAnimal(animalData);
        const newAnimal = response.data;
        setAnimals(prev => [...prev, newAnimal]);
      } else {
        const response = await animalsAPI.updateAnimal(selectedAnimal!.id, animalData);
        const updatedAnimal = response.data;
        setAnimals(prev => prev.map(animal => 
          animal.id === selectedAnimal?.id 
            ? updatedAnimal
            : animal
        ));
      }
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error saving animal:', error);
      alert('Eroare la salvarea animalului');
    } finally {
      setIsSaving(false);
    }
  };

  // const handleAdvancedSearch = (filters: any) => {
  //   let filtered = animals;

  //   // Apply all the advanced filters
  //   if (filters.searchTerm) {
  //     filtered = filtered.filter(animal =>
  //       animal.name.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
  //       animal.description.toLowerCase().includes(filters.searchTerm.toLowerCase())
  //     );
  //   }

  //   if (filters.gender) {
  //     filtered = filtered.filter(animal => animal.gender === filters.gender);
  //   }

  //   if (filters.size) {
  //     filtered = filtered.filter(animal => animal.size === filters.size);
  //   }

  //   if (filters.status) {
  //     filtered = filtered.filter(animal => animal.status === filters.status);
  //   }

  //   if (filters.ageMin || filters.ageMax) {
  //     filtered = filtered.filter(animal => 
  //       animal.age >= filters.ageMin && animal.age <= filters.ageMax
  //     );
  //   }

  //   if (filters.vaccinated !== null) {
  //     filtered = filtered.filter(animal => animal.vaccinated === filters.vaccinated);
  //   }

  //   if (filters.neutered !== null) {
  //     filtered = filtered.filter(animal => animal.neutered === filters.neutered);
  //   }

  //   if (filters.chipped !== null) {
  //     filtered = filtered.filter(animal => animal.chipped === filters.chipped);
  //   }

  //   if (filters.medicalConditions.length > 0) {
  //     filtered = filtered.filter(animal => 
  //       filters.medicalConditions.some((condition: string) => 
  //         animal.medicalHistory.some(record => 
  //           record.description.toLowerCase().includes(condition.toLowerCase()) ||
  //           record.type.toLowerCase().includes(condition.toLowerCase())
  //         )
  //       )
  //     );
  //   }

  //   setFilteredAnimals(filtered);
  // }; // Commented out for future use

  // Statistics - use dashboard stats for overall totals, not current page data
  const availableAnimals = Number(dashboardStats.byStatus?.available || 0);
  const adoptedAnimals = Number(dashboardStats.byStatus?.adopted || 0);
  const pendingAnimals = Number(dashboardStats.byStatus?.hospital || 0) + Number(dashboardStats.byStatus?.quarantine || 0);
  const reservedAnimals = Number(dashboardStats.byStatus?.reserved || 0);

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Animale</h1>
            <p className="text-gray-600">Gestionează animalele din adăpost</p>
          </div>
          <div className="flex space-x-3">
            {/* <Button variant="outline" onClick={() => setIsAdvancedSearchOpen(true)}>
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              Căutare avansată
            </Button> */}
            <Button variant="primary" onClick={handleAddAnimal}>
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Adaugă animal
            </Button>
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-primary-100 rounded-lg">
                <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total animale</p>
                <p className="text-2xl font-bold text-gray-900">{Number(dashboardStats.total || 0)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Disponibile</p>
                <p className="text-2xl font-bold text-gray-900">{availableAnimals}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">În proces</p>
                <p className="text-2xl font-bold text-gray-900">{pendingAnimals}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Rezervate</p>
                <p className="text-2xl font-bold text-gray-900">{reservedAnimals}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Adoptate</p>
                <p className="text-2xl font-bold text-gray-900">{adoptedAnimals}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and View Toggle */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
            <div className="flex flex-col md:flex-row md:items-center space-y-4 md:space-y-0 md:space-x-4">
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Caută după nume sau descriere... (apasă Enter)"
                  className="w-full md:w-80 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <button
                  onClick={handleSearch}
                  className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  Caută
                </button>
              </div>

              <div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as 'all' | 'available' | 'adopted' | 'hospital' | 'quarantine' | 'reserved')}
                  className="w-full md:w-auto px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="all">Toate statusurile</option>
                  <option value="available">Disponibil</option>
                  <option value="adopted">Adoptat</option>
                  <option value="hospital">În tratament</option>
                  <option value="quarantine">În quarantină</option>
                  <option value="reserved">Rezervat</option>
                </select>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md ${
                  viewMode === 'grid' 
                    ? 'bg-primary-100 text-primary' 
                    : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-md ${
                  viewMode === 'list' 
                    ? 'bg-primary-100 text-primary' 
                    : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Animals Display */}
        {filteredAnimals.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Nu au fost găsite animale</h3>
            <p className="mt-1 text-sm text-gray-500">
              Nu există animale care să corespundă criteriilor de căutare.
            </p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredAnimals.map((animal) => (
              <div key={animal.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                <img
                  src={getImageUrl(animal.id)}
                  alt={animal.name}
                  className="w-full h-48 object-cover"
                  onError={(e) => {
                    e.currentTarget.src = '/images/default-animal.svg';
                    e.currentTarget.onerror = null;
                  }}
                />
                <div className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{animal.name}</h3>
                    {getStatusBadge(animal.status)}
                  </div>
                  
                  <div className="space-y-1 text-sm text-gray-600 mb-3">
                    <p><span className="font-medium">Vârstă:</span> {animal.age}</p>
                    <p><span className="font-medium">Gen:</span> {animal.gender === 'male' ? 'Mascul' : 'Femelă'}</p>
                    <p><span className="font-medium">Mărime:</span> {animal.size}</p>
                  </div>

                  <div className="flex items-center space-x-2 mb-3">
                    {animal.vaccinated && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Vaccinat
                      </span>
                    )}
                    {animal.neutered && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-dark">
                        Sterilizat
                      </span>
                    )}
                    {animal.chipped && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        Cipat
                      </span>
                    )}
                  </div>

                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{animal.description}</p>

                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-primary">{animal.totalInvested} RON</span>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEditAnimal(animal)}
                        className="text-primary hover:text-primary-dark text-sm font-medium"
                      >
                        Editează
                      </button>
                      <button
                        onClick={() => handleDeleteAnimal(animal.id)}
                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                      >
                        Șterge
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Animal
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Detalii
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status medical
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                              Informații
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acțiuni
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredAnimals.map((animal) => (
                    <tr key={animal.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <img 
                            className="h-10 w-10 rounded-full object-cover" 
                            src={getImageUrl(animal.id)} 
                            alt={animal.name}
                            onError={(e) => {
                              e.currentTarget.src = '/images/default-animal.svg';
                              e.currentTarget.onerror = null;
                            }}
                          />
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{animal.name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {animal.age}, {animal.gender === 'male' ? 'Mascul' : 'Femelă'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {animal.size}, {animal.color}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col space-y-1">
                          {animal.vaccinated && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Vaccinat
                            </span>
                          )}
                          {animal.neutered && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-dark">
                              Sterilizat
                            </span>
                          )}
                          {animal.chipped && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              Cipat
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(animal.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {animal.totalInvested} RON
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handleEditAnimal(animal)}
                            className="text-primary hover:text-primary-dark"
                          >
                            Editează
                          </button>
                          <button
                            onClick={() => handleDeleteAnimal(animal.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Șterge
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination Controls */}
        {!isLoading && totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between sm:px-6 rounded-lg shadow">
            <div className="flex-1 flex items-center justify-between sm:hidden">
              <button
                onClick={() => loadAnimals(currentPage - 1)}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                  currentPage === 1
                    ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                    : 'text-gray-700 bg-white hover:bg-gray-50'
                }`}
              >
                Anterior
              </button>
              <div className="flex items-center">
                <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm font-medium rounded-full">
                  {currentPage}/{totalPages}
                </span>
              </div>
              <button
                onClick={() => loadAnimals(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                  currentPage === totalPages
                    ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                    : 'text-gray-700 bg-white hover:bg-gray-50'
                }`}
              >
                Următor
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Afișare{' '}
                  <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> la{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * itemsPerPage, totalCount)}
                  </span>{' '}
                  din <span className="font-medium">{totalCount}</span> câini
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => loadAnimals(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 text-sm font-medium ${
                      currentPage === 1
                        ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                        : 'text-gray-500 bg-white hover:bg-gray-50'
                    }`}
                  >
                    <span className="sr-only">Previous</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                  
                  {/* Page Numbers */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => loadAnimals(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          pageNum === currentPage
                            ? 'z-10 bg-primary text-white border-primary'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => loadAnimals(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 text-sm font-medium ${
                      currentPage === totalPages
                        ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                        : 'text-gray-500 bg-white hover:bg-gray-50'
                    }`}
                  >
                    <span className="sr-only">Next</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Animal Modal */}
      <AnimalModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveAnimal}
        animal={selectedAnimal}
        mode={modalMode}
      />

      {/* Advanced Search Modal - Commented out for future use */}
      {/* <AdvancedSearch
        searchType="animals"
        isOpen={isAdvancedSearchOpen}
        onClose={() => setIsAdvancedSearchOpen(false)}
        onSearch={handleAdvancedSearch}
        onReset={() => setFilteredAnimals(animals)}
      /> */}
    </Layout>
  );
};

export default Animals; 